import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import React from "react";
import { useForm } from "react-hook-form";

import { persistAuthUserSessionMirror } from "@/client/auth-storage";
import { useToast } from "@/components/ui/use-toast";
import { useConfirmDialog, useI18n } from "@/context";

import * as usersApi from "./users.api";
import { IUseClients } from "./users.hooks.interface";
import {
  knowledgeBaseFormSchema,
  TKnowledgeBaseFormData,
  TUserFormData,
  userFormSchema,
} from "./users.schema";
import { TCreateUserInput, TUser } from "./users.types";
export const useUsers = (): IUseClients => {
  const { t } = useI18n();
  const { toast } = useToast();
  const { openDialog } = useConfirmDialog();
  const queryClient = useQueryClient();

  const [isClientFormOpen, setIsClientFormOpen] =
    React.useState<boolean>(false);
  const [selectedClient, setSelectedClient] = React.useState<TUser | null>(
    null
  );
  const [activeTab, setActiveTab] = React.useState<string>("clients");

  const clientForm = useForm<TUserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const knowledgeBaseForm = useForm<TKnowledgeBaseFormData>({
    resolver: zodResolver(knowledgeBaseFormSchema),
    defaultValues: {
      name: "",
      clientId: "",
      files: undefined,
    },
  });

  React.useEffect(() => {
    if (selectedClient) {
      clientForm.reset({
        name: selectedClient.first_name,
      });
    } else {
      clientForm.reset({
        name: "",
      });
    }
  }, [selectedClient, clientForm]);

  const { data: users, error: clientsError } = useQuery({
    queryKey: ["users"],
    queryFn: usersApi.getAllUsers,
  });

  const { data: aiProviders, error: providersError } = useQuery({
    queryKey: ["aiProviders"],
    queryFn: usersApi.getAllAiProviders,
    enabled: isClientFormOpen,
  });

  React.useEffect(() => {
    if (clientsError) {
      toast({
        title: t("generic.error"),
        description: clientsError.message,
        variant: "destructive",
      });
    }
  }, [clientsError, toast, t]);

  React.useEffect(() => {
    if (providersError) {
      toast({
        title: t("generic.error"),
        description: providersError.message,
        variant: "destructive",
      });
    }
  }, [providersError, toast, t]);

  const createClientMutation = useMutation({
    mutationFn: usersApi.createUser,
    onSuccess: (data: TUser & { refreshToken: string }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      const authUser = JSON.parse(Cookies.get("authUser") || "{}");
      if (authUser) {
        const updated = JSON.stringify({
          ...authUser,
          token: data.refreshToken,
        });
        Cookies.set("authUser", updated);
        persistAuthUserSessionMirror(updated);
      }
      setIsClientFormOpen(false);
      clientForm.reset();
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerCreated"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("clients.errors.createFailed"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: TCreateUserInput;
    }) => usersApi.updateUser(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setSelectedClient(null);
      setIsClientFormOpen(false);
      clientForm.reset();
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerUpdated"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("clients.errors.updateFailed"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: usersApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerDeleted"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const uploadKnowledgeBaseMutation = useMutation({
    mutationFn: ({
      clientId,
      name,
      files,
    }: {
      clientId: string;
      name: string;
      files: File[];
    }) => usersApi.uploadKnowledgeBase(clientId, name, files),
    onSuccess: () => {
      knowledgeBaseForm.reset();
      toast({
        title: t("generic.success"),
        description: t("knowledgeBase.success.filesUploaded"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const providerOptions = React.useMemo(() => {
    if (!Array.isArray(aiProviders)) return [];
    return aiProviders.map((provider) => ({
      value: provider.aiProviderId,
      label: provider.name,
    }));
  }, [aiProviders]);

  const clientOptions = React.useMemo(() => {
    if (!Array.isArray(users)) return [];

    return users.map((user) => ({
      value: user.id,
      label: user.first_name,
    }));
  }, [users]);

  const createClient = React.useCallback(
    (data: TCreateUserInput) => {
      createClientMutation.mutate(data);
    },
    [createClientMutation]
  );

  const updateClient = React.useCallback(
    (clientId: string, data: TCreateUserInput) => {
      updateClientMutation.mutate({ clientId, data });
    },
    [updateClientMutation]
  );

  const deleteClient = React.useCallback(
    (clientId: string) => {
      const user = users?.find((user) => user.id === clientId);
      openDialog(t("generic.confirmDelete"), user?.first_name || "", () =>
        deleteClientMutation.mutate(clientId)
      );
    },
    [deleteClientMutation, openDialog, t, users]
  );

  const selecTUser = React.useCallback((user: TUser) => {
    setSelectedClient(user);
    setIsClientFormOpen(true);
  }, []);

  const openClientForm = React.useCallback(() => {
    setSelectedClient(null);
    setIsClientFormOpen(true);
  }, []);

  const closeClientForm = React.useCallback(() => {
    setIsClientFormOpen(false);
    setSelectedClient(null);
  }, []);

  const handleClientFormSubmit = React.useCallback(
    (values: TCreateUserInput) => {
      if (selectedClient) {
        updateClient(selectedClient.id, values);
        return;
      }
      createClient(values);
    },
    [createClient, updateClient, selectedClient]
  );

  const handleKnowledgeBaseFormSubmit = React.useCallback(
    (data: TKnowledgeBaseFormData) => {
      if (data.files && data.files.length > 0) {
        uploadKnowledgeBaseMutation.mutate({
          clientId: data.clientId,
          name: data.name,
          files: Array.from(data.files),
        });
      }
    },
    [uploadKnowledgeBaseMutation]
  );

  const handleCopyApiKey = React.useCallback(
    (apiKey: string) => {
      navigator.clipboard.writeText(apiKey);
      toast({
        title: t("generic.success"),
        description: t("clients.success.apiKeyCopied"),
      });
    },
    [t, toast]
  );

  const usersList = React.useMemo(
    () => (Array.isArray(users) ? users : []),
    [users],
  );

  return {
    users: usersList,
    isClientFormOpen,
    selectedClient,
    activeTab,
    clientForm,
    knowledgeBaseForm,
    providerOptions,
    clientOptions,
    createClient,
    updateClient,
    deleteClient,
    selecTUser,
    openClientForm,
    closeClientForm,
    setActiveTab,
    handleClientFormSubmit,
    handleKnowledgeBaseFormSubmit,
    handleCopyApiKey,
  };
};
